import React, { useEffect, useMemo, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  DatePicker,
  message,
  Space,
  Image,
  Upload,
  Row,
  Col,
  InputNumber,
  Select
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import './index.css';


const HealthCertificates = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const [filters, setFilters] = useState({
    q: ''
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

    // 获取接待助理的列表
  const [receptionistOptions, setReceptionistOptions] = useState([]);
  useEffect(() => {
      axios.get('/api/users/rolesoptions').then(res => {
        const data = res?.data?.data || {};
        // 只过滤出 role 为 service 的用户
        const serviceUsers = data.service || [];
        setReceptionistOptions(serviceUsers);
      }).catch(() => {
        setReceptionistOptions([]);
      });
  }, []);

  // 获取下单用户的列表
  const [userOptions, setUserOptions] = useState([]);

  useEffect(() => {
    if (modalOpen) {
      axios.get('/api/place-orders/usersoptions').then(res => {
        const arr = res?.data?.data || [];
        setUserOptions(arr);
      }).catch(() => {
        setUserOptions([]);
      });
    }
  }, [modalOpen]);



  const normFile = (e) => {
    if (Array.isArray(e)) return e;
    return e && e.fileList;
  };

  const queryParams = useMemo(() => {
    return {
      page: pagination.current,
      per_page: pagination.pageSize,
      q: filters.q?.trim() || undefined
    };
  }, [filters, pagination.current, pagination.pageSize]);

  const loadData = async (
    page = pagination.current,
    pageSize = pagination.pageSize,
    qOverride = filters.q
  ) => {
    setLoading(true);
    try {
      const res = await axios.get('/api/health-certificates', {
        params: {
          page,
          per_page: pageSize,
          q: qOverride?.trim() || undefined
        },
      });

      const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
      const total = Array.isArray(res.data) ? data.length : res.data?.total || 0;

      setList(data);
      setPagination(prev => ({ ...prev, current: page, pageSize, total }));
    } catch (e) {
      message.error('加载失败');
      setList([]);
      setPagination(prev => ({ ...prev, total: 0 }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPagination(p => ({ ...p, current: 1 }));
    loadData(1, pagination.pageSize);
  }, [pagination.pageSize]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  // 点击编辑按钮
   const openEdit = (record) => {
    setEditing(record);
    
    // 查找接待助理对应的选项，以便正确设置下拉框的值
    const receptionistOption = receptionistOptions.find(
      option => option.id === Number(record.receptionist)
    );
    
    console.log('receptionistOption', receptionistOption);
    form.setFieldsValue({
      ...record,
      create_time: record.create_time ? dayjs(record.create_time) : null,
      // 设置下拉框的值为选项的value（即用户ID），这样下拉框会正确显示对应的label
      receptionist: receptionistOption ? receptionistOption.id : null,
    });
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/health-certificates/${id}`);
      message.success('删除成功');
      loadData(pagination.current, pagination.pageSize);
    } catch {
      message.error('删除失败');
    }
  };

  // frontend/src/views/HealthCertificates/index.jsx
  const handleSubmit = async (values) => {
    try {
      if (editing) {
        const payload = {
          ...values,
          create_time: values.create_time ? values.create_time.format('YYYY-MM-DD') : null,
          amount: values.amount != null ? Number(values.amount) : null,
        };
        // 编辑时如未改图，可继续走 JSON；如要改图，可同创建一样改用 FormData
        await axios.put(`/api/health-certificates/${editing.id}`, payload);
        message.success('更新成功');
      } else {
        const fd = new FormData();
        
        fd.append('create_time', values.create_time ? values.create_time.format('YYYY-MM-DD') : '');
        fd.append('user_id', values.user_id ?? '');
        fd.append('user_name', values.user_name ?? '');
        fd.append('amount', values.amount != null ? Number(values.amount) : '');
        fd.append('receptionist', values.receptionist ?? '');


        const avatarFile = values.avatar?.[0]?.originFileObj;
        const certFile = values.certificate?.[0]?.originFileObj;

        if (avatarFile) fd.append('avatar', avatarFile);
        if (certFile) fd.append('certificate', certFile);

        await axios.post('/api/health-certificates', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        message.success('创建成功');
      }

      setModalOpen(false);
      loadData(pagination.current, pagination.pageSize);
    } catch {
      message.error(editing ? '更新失败' : '创建失败');
    }
  };

  const columns = [
    {
      title: '创建时间',
      dataIndex: 'create_time',
      key: 'create_time',
      width: 160,
      render: v => (v ? dayjs(v).format('YYYY/MM/DD') : '-'),
    },
    {
      title: '下单用户',
      dataIndex: 'user_name',
      key: 'user_name',
      width: 160,
      ellipsis: true,
    },
    {
      title: '接待助理',
      dataIndex: 'receptionist_username',
      key: 'receptionist_username',
      width: 160,
      ellipsis: true,
    },
    {
      title: '头像',
      dataIndex: 'avatar_path',
      key: 'avatar_path',
      width: 140,
      render: (url) =>
        url ? (
          <Image
            src={url}
            width={72}
            height={72}
            style={{ objectFit: 'cover', borderRadius: 8 }}
            placeholder
          />
        ) : (
          '-'
        ),
    },
    {
      title: '健康证',
      dataIndex: 'health_certificate_path',
      key: 'health_certificate_path',
      width: 180,
      render: (url) =>
        url ? (
          <Image
            src={url}
            width={140}
            height={84}
            style={{ objectFit: 'cover', borderRadius: 6 }}
            placeholder
          />
        ) : (
          '-'
        ),
    },
    {
      title: '金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 100,
      render: v => (v == null ? '-' : v),
    },
    {
      title: '操作',
      key: 'action',
      width: 160,
      render: (_, record) => (
        <Space size="small">
          <Button type="link" icon={<EditOutlined />} onClick={() => openEdit(record)}>
            编辑
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];



  return (
    <div className="health-certificates-container">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Input.Search
          style={{ width: 260 }}
          placeholder="按 用户/金额 模糊搜索"
          value={filters.q}
          allowClear
          enterButton={<SearchOutlined />}
          onChange={({ target: { value } }) => {
            setFilters(prev => ({ ...prev, q: value }));
            if (value === '') {
              setPagination(p => ({ ...p, current: 1, pageSize: 10 }));
              loadData(1, 10, '');
            }
          }}
          onSearch={() => {
            setPagination(p => ({ ...p, current: 1 }));
            loadData(1, pagination.pageSize, filters.q);
          }}
        />

        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          新增
        </Button>
      </div>

      <div className="table-container">
        <Table
          columns={columns}
          dataSource={list}
          rowKey="id"
          loading={loading}
          size='small'
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (t) => `共 ${t} 条记录`,
            onChange: (p, ps) => {
              setPagination((prev) => ({ ...prev, current: p, pageSize: ps }));
              loadData(p, ps);
            },
          }}
        />
      </div>

      <Modal
        title={editing ? '编辑健康信息' : '新增健康信息'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="create_time"
                label="创建时间"
                rules={[{ required: true, message: '请选择创建时间' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="user_id"
                label="下单用户"
                rules={[{ required: true, message: '请选择下单用户' }]}
              >
                <Select
                  placeholder="请选择下单用户"
                  showSearch
                  options={userOptions}
                  optionFilterProp="label"
                  onChange={(value, option) => {
                    // 选择后同时写入 user_id 与 user_name
                    form.setFieldsValue({
                      user_id: value,
                      user_name: option?.label || '',
                    });
                  }}
                />
              </Form.Item>
              {/* 隐藏域，随选择自动带上 user_name */}
              <Form.Item name="user_name" style={{ display: 'none' }}>
                <Input />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="receptionist"
                label="接待助理"
                rules={[{ required: true, message: '请选择接待助理' }]}
              >
                <Select
                  placeholder="请选择接待助理"
                  showSearch
                  options={receptionistOptions}
                  optionFilterProp="label"
                  onChange={(value, option) => {
                    // 保存选中的完整对象（包含value和label）
                    form.setFieldsValue({
                      receptionist: option?.value || '', // 实际保存用户名
                    });
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="avatar"
                label="头像上传"
                valuePropName="fileList"
                getValueFromEvent={normFile}
                rules={[{ required: true, message: '请上传头像' }]}
              >
                <Upload
                  accept="image/*"
                  listType="picture-card"
                  maxCount={1}
                  beforeUpload={() => false} // 阻止自动上传
                >
                  + 上传
                </Upload>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="certificate"
                label="健康证上传"
                valuePropName="fileList"
                getValueFromEvent={normFile}
                // rules={[{ required: true, message: '请上传健康证' }]}
              >
                <Upload
                  accept="image/*"
                  maxCount={1}
                  beforeUpload={() => false} // 阻止自动上传
                >
                  <Button icon={<UploadOutlined />}>选择文件</Button>
                </Upload>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="amount"
            label="金额"
            rules={[{ required: true, message: '请输入金额' }]}
          >
            <InputNumber style={{ width: '100%' }} placeholder="请输入金额" min={0} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button onClick={() => setModalOpen(false)}>取消</Button>
              <Button type="primary" htmlType="submit">
                {editing ? '更新' : '创建'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default HealthCertificates;